import { v4 as uuid } from 'uuid';

import {
  EmployeeFilterFragment,
  JobStageInterviewSeatType,
  ModuleSeat as ModuleSeatGql,
  TrainingStatus,
} from 'src/generated/mloop-graphql';

import { AttributeMap, JobStageInterviewSeat, TRAINEE_REQUIREMENT } from 'src/store/slices/job-stage-interview-seat';

type SeatAttributeInput = { attributeNameId?: string | null; attributeTagValueId?: string | null };

/**
 * Minimal structural shape of a module seat fragment. Both `ModuleSeat_seat` (read popover) and
 * `BaseEditInterviewSeatModule_seat` (inline edit panel) satisfy this, so the adapters work for both
 * without coupling to a single generated fragment type.
 */
export type ModuleSeatFragmentLike = {
  id: string;
  moduleSeat?: {
    interviewModuleId?: string | null;
    interviewModule?: unknown;
    selectedEmployeeIds?: readonly string[] | null;
    jobStageInterviewSeatAttributes?: readonly SeatAttributeInput[] | null;
    trainedConfig?: ModuleSeatConfigLike | null;
    shadowConfig?: ModuleSeatConfigLike | null;
    reverseShadowConfig?: ModuleSeatConfigLike | null;
    traineeRequirement?: string | null;
    traineeSchedulingStrategy?: string | null;
  } | null;
};

type ModuleSeatConfigLike = {
  selectedEmployeeIds?: readonly string[] | null;
  selectedAttributes?: readonly SeatAttributeInput[] | null;
};

type PersistedSeatAttribute = {
  id: string;
  jobStageInterviewSeatId: string;
  attributeNameId: string;
  attributeTagValueId: string;
};

const toAttributeMap = (attributes?: readonly SeatAttributeInput[] | null): AttributeMap => {
  const result: AttributeMap = {};
  (attributes ?? []).forEach((attribute) => {
    if (!attribute.attributeNameId || !attribute.attributeTagValueId) {
      return;
    }
    if (!result[attribute.attributeNameId]) {
      result[attribute.attributeNameId] = [];
    }
    result[attribute.attributeNameId].push(attribute.attributeTagValueId);
  });
  return result;
};

const selectedEmployeeIdsForStatus = ({
  allInterviewModuleMembers,
  legacySelectedEmployeeIds,
  configSelectedEmployeeIds,
  status,
}: {
  allInterviewModuleMembers: EmployeeFilterFragment[];
  legacySelectedEmployeeIds: readonly string[];
  configSelectedEmployeeIds?: readonly string[] | null;
  status: TrainingStatus;
}): string[] => {
  if (configSelectedEmployeeIds !== undefined && configSelectedEmployeeIds !== null) {
    return [...configSelectedEmployeeIds];
  }

  return allInterviewModuleMembers
    .filter((member) => member.status === status && legacySelectedEmployeeIds.includes(member.employeeId))
    .map((member) => member.employeeId);
};

/**
 * Builds the store-shaped JobStageInterviewSeat that the filter UIs operate on from a module seat
 * fragment. Used by the FF-on SeatSettings panel and ModuleSeatV2 card. Falls back to legacy
 * `selectedEmployeeIds` / `jobStageInterviewSeatAttributes` for trained-role data when per-role
 * configs are unset.
 */
export const moduleSeatFragmentToJobStageInterviewSeat = (
  seat: ModuleSeatFragmentLike,
  seatIndex: number,
  allInterviewModuleMembers: EmployeeFilterFragment[]
): JobStageInterviewSeat => {
  const legacySelectedEmployeeIds = seat.moduleSeat?.selectedEmployeeIds ?? [];
  const trainedSelectedAttributes = seat.moduleSeat?.trainedConfig?.selectedAttributes;
  // An empty (not just absent) trained config attribute list must still fall back to the legacy
  // jobStageInterviewSeatAttributes, otherwise saved legacy filters are silently dropped.
  const attributeMap = toAttributeMap(
    trainedSelectedAttributes?.length ? trainedSelectedAttributes : seat.moduleSeat?.jobStageInterviewSeatAttributes
  );
  const shadowAttributes = toAttributeMap(seat.moduleSeat?.shadowConfig?.selectedAttributes);
  const reverseShadowAttributes = toAttributeMap(seat.moduleSeat?.reverseShadowConfig?.selectedAttributes);
  const shadowAttributeMap = Object.keys(shadowAttributes).length > 0 ? shadowAttributes : undefined;
  const reverseShadowAttributeMap =
    Object.keys(reverseShadowAttributes).length > 0 ? reverseShadowAttributes : undefined;

  const selectedTrainedEmployeeIds = selectedEmployeeIdsForStatus({
    allInterviewModuleMembers,
    legacySelectedEmployeeIds,
    configSelectedEmployeeIds: seat.moduleSeat?.trainedConfig?.selectedEmployeeIds,
    status: TrainingStatus.Trained,
  });
  const selectedShadowEmployeeIds = selectedEmployeeIdsForStatus({
    allInterviewModuleMembers,
    legacySelectedEmployeeIds,
    configSelectedEmployeeIds: seat.moduleSeat?.shadowConfig?.selectedEmployeeIds,
    status: TrainingStatus.Shadow,
  });
  const selectedReverseShadowEmployeeIds = selectedEmployeeIdsForStatus({
    allInterviewModuleMembers,
    legacySelectedEmployeeIds,
    configSelectedEmployeeIds: seat.moduleSeat?.reverseShadowConfig?.selectedEmployeeIds,
    status: TrainingStatus.ReverseShadow,
  });

  return {
    id: seat.id,
    jobStageInterviewId: '', // Not used by the filter UIs.
    index: seatIndex,
    type: JobStageInterviewSeatType.Module,
    interviewId: seat.moduleSeat?.interviewModuleId ?? undefined,
    attributeMap,
    shadowAttributeMap,
    reverseShadowAttributeMap,
    traineeRequirement: (seat.moduleSeat?.traineeRequirement ??
      undefined) as JobStageInterviewSeat['traineeRequirement'],
    traineeSchedulingStrategy: (seat.moduleSeat?.traineeSchedulingStrategy ??
      undefined) as JobStageInterviewSeat['traineeSchedulingStrategy'],
    selectedTrainedInterviewerIds: selectedTrainedEmployeeIds,
    selectedShadowInterviewerIds: selectedShadowEmployeeIds,
    selectedReverseShadowInterviewerIds: selectedReverseShadowEmployeeIds,
    moduleSeat: {
      interviewModuleId: seat.moduleSeat?.interviewModuleId,
      interviewModule: seat.moduleSeat?.interviewModule,
      selectedEmployeeIds: legacySelectedEmployeeIds.length ? [...legacySelectedEmployeeIds] : undefined,
      jobStageInterviewSeatAttributes: seat.moduleSeat?.jobStageInterviewSeatAttributes,
      trainedConfig: seat.moduleSeat?.trainedConfig,
      shadowConfig: seat.moduleSeat?.shadowConfig,
      reverseShadowConfig: seat.moduleSeat?.reverseShadowConfig,
      traineeRequirement: seat.moduleSeat?.traineeRequirement,
      traineeSchedulingStrategy: seat.moduleSeat?.traineeSchedulingStrategy,
    } as ModuleSeatGql,
  };
};

const toSeatAttributes = (seatId: string, attributeMap?: AttributeMap): PersistedSeatAttribute[] =>
  Object.keys(attributeMap ?? {}).flatMap((attributeNameId) =>
    (attributeMap?.[attributeNameId] ?? []).map((attributeTagValueId) => ({
      id: uuid(),
      jobStageInterviewSeatId: seatId,
      attributeNameId,
      attributeTagValueId,
    }))
  );

/**
 * Maps an edited store-shaped seat back into the module seat fragment shape that the interview-plan
 * tree's onUpdated callback expects. Used by the FF-on SeatSettings panel. Writes both legacy trained
 * fields and per-role config fields so existing consumers keep working.
 */
export const jobStageInterviewSeatToModuleSeatUpdate = (
  seatId: string,
  updatedSeat: JobStageInterviewSeat
): { id: string; moduleSeat?: ModuleSeatGql | null } => {
  const jobStageInterviewSeatAttributes = toSeatAttributes(seatId, updatedSeat.attributeMap);
  const shadowJobStageInterviewSeatAttributes = toSeatAttributes(seatId, updatedSeat.shadowAttributeMap);
  const reverseShadowJobStageInterviewSeatAttributes = toSeatAttributes(seatId, updatedSeat.reverseShadowAttributeMap);
  const traineeRequirement =
    updatedSeat.traineeRequirement ??
    ((updatedSeat.moduleSeat?.traineeRequirement ?? undefined) as JobStageInterviewSeat['traineeRequirement']);
  const traineeSchedulingStrategy =
    updatedSeat.traineeSchedulingStrategy ??
    ((updatedSeat.moduleSeat?.traineeSchedulingStrategy ??
      undefined) as JobStageInterviewSeat['traineeSchedulingStrategy']);
  const legacySelectedEmployeeIds = [
    ...(updatedSeat.selectedTrainedInterviewerIds ?? []),
    ...(updatedSeat.selectedShadowInterviewerIds ?? []),
    ...(updatedSeat.selectedReverseShadowInterviewerIds ?? []),
  ];

  return {
    id: updatedSeat.id,
    moduleSeat: {
      interviewModuleId: updatedSeat.moduleSeat?.interviewModuleId,
      interviewModule: updatedSeat.moduleSeat?.interviewModule,
      selectedEmployeeIds: legacySelectedEmployeeIds,
      jobStageInterviewSeatAttributes: jobStageInterviewSeatAttributes.length
        ? jobStageInterviewSeatAttributes
        : undefined,
      // Attributes win only when there actually are attribute filters; otherwise persist the
      // (possibly empty) people selection. An empty People selection is "any member" and must
      // round-trip as an empty people list, not an empty attributes config.
      trainedConfig: (jobStageInterviewSeatAttributes.length
        ? { selectedAttributes: jobStageInterviewSeatAttributes }
        : { selectedEmployeeIds: updatedSeat.selectedTrainedInterviewerIds ?? [] }) as ModuleSeatGql['trainedConfig'],
      shadowConfig: (shadowJobStageInterviewSeatAttributes.length
        ? { selectedAttributes: shadowJobStageInterviewSeatAttributes }
        : { selectedEmployeeIds: updatedSeat.selectedShadowInterviewerIds ?? [] }) as ModuleSeatGql['shadowConfig'],
      reverseShadowConfig: (reverseShadowJobStageInterviewSeatAttributes.length
        ? { selectedAttributes: reverseShadowJobStageInterviewSeatAttributes }
        : {
            selectedEmployeeIds: updatedSeat.selectedReverseShadowInterviewerIds ?? [],
          }) as ModuleSeatGql['reverseShadowConfig'],
      traineeRequirement,
      traineeSchedulingStrategy:
        traineeRequirement === TRAINEE_REQUIREMENT.NONE ? undefined : traineeSchedulingStrategy,
    } as ModuleSeatGql,
  };
};
